import { Link, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Sparkle, ArrowLeft } from '@phosphor-icons/react';
import { useLanguage } from '@/context/LanguageContext';

const EN_TITLES = {
  kvkk: 'Privacy Notice (KVKK)',
  terms: 'Terms of Use',
  cookies: 'Cookie Policy',
};

const EN_BODIES = {
  // KVKK stays Turkish (binding under Turkish law); we keep it as-is.
  kvkk: null,

  terms: `
## 1. Service Description
GrowthRIQ is a SaaS platform that provides AI-powered growth intelligence to e-commerce and e-export businesses. It includes assessment, AI report generation, store audit and advisory modules.

## 2. Membership
- You must be 18 years of age or older.
- You agree to provide accurate and up-to-date information.
- Account security is your responsibility; do not share your password.

## 3. Payment & Subscription
- Paid packages can be one-time or subscription-based.
- Payments are processed securely via İyzico / PayTR (Turkish payment infrastructure).
- Refund policy: Provided the AI-generated report has not been downloaded or consumed, a refund can be issued within 14 days of purchase.

## 4. Unacceptable Use
- You may not use the platform for any unlawful activity.
- Reverse-engineering, scraping, or unauthorized access attempts are prohibited.
- You may not use AI outputs to build a competing product.

## 5. Intellectual Property
The GrowthRIQ brand, logo, assessment questions and AI report templates are property of the platform. Your individual reports belong to you; if you publish them commercially, please credit us as the source.

## 6. Limitation of Liability
AI reports contain interpretations and suggestions; the responsibility for your financial decisions rests with you. To the maximum extent permitted by law, we are not liable for data loss or indirect damages.

## 7. Termination
You may terminate the service at any time, without giving reasons, by using the "delete account" button.

## 8. Disputes
This agreement is governed by the laws of the Republic of Turkey. The courts of Istanbul shall have jurisdiction.
`,

  cookies: `
## 1. What is a Cookie?
A cookie is a small text file placed on your browser when you visit a website. It helps us remember your preferences across pages and analyze traffic.

## 2. Cookie Types We Use

### Essential cookies (always active)
- **growthriq_token** — user session identity (JWT)
- **growthriq_lang** — language preference (TR/EN)
- **growthriq_cookie_consent** — cookie consent status

These cookies are required for the platform to function and cannot be turned off.

### Optional / Analytics cookies
We do not currently use any external analytics service (Google Analytics, etc.). When we do, we will ask for your consent.

### Marketing cookies
We do not use them.

## 3. Cookie Consent
You can give consent via the banner shown on your first visit. You can withdraw consent at any time by clearing cookies.

## 4. Browser Settings
Modern browsers allow you to disable cookies entirely. However, the platform may not work correctly if essential cookies are disabled.

## 5. Contact
For questions: **kvkk@growthriq.net**
`,
};

const CONTENT = {
  kvkk: {
    title: 'KVKK Aydınlatma Metni',
    updated: '1 Şubat 2026',
    body: `
## 1. Veri Sorumlusu
Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti. ("**GrowthRIQ**" markası ile faaliyet gösteren Platform), 6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") kapsamında veri sorumlusu olarak hareket etmektedir.

- **Adres:** Rami Yenimahalle, Palabıyık Sk. No: 22/B D: 13, Eyüpsultan / İstanbul
- **Vergi Dairesi / No:** Bayrampaşa Vergi Dairesi / 2711882508
- **MERSIS No:** 0271188250800001
- **KEP:** datinova@hs09.kep.tr

## 2. İşlenen Kişisel Veriler
- **Kimlik bilgileri:** Ad, soyad
- **İletişim bilgileri:** E-posta, telefon (opsiyonel)
- **Müşteri işlem bilgileri:** Asesman cevapları, rapor verileri, ödeme kayıtları
- **İşlem güvenliği bilgileri:** IP adresi, log kayıtları
- **Pazarlama bilgileri:** Çerez verileri, kullanım analitiği

## 3. İşleme Amaçları
- Hizmetin sunulması ve sözleşmenin ifası
- Üyelik hesabınızın oluşturulması ve yönetilmesi
- AI destekli rapor üretimi ve danışmanlık
- Faturalandırma ve ödeme işlemleri
- Yasal yükümlülüklerin yerine getirilmesi
- İletişim faaliyetleri ve müşteri memnuniyeti

## 4. Aktarım
Verileriniz, hizmet sağlayıcılarımız (ödeme, e-posta, AI altyapısı) ile yalnızca işin yürütülmesi için zorunlu olduğunda aktarılır. Hiçbir üçüncü tarafa pazarlama amacıyla satılmaz.

## 5. Haklarınız
KVKK m.11 uyarınca: verilerinize erişme, düzeltme, silme, işlenmesini durdurma ve hesabınızı tamamen silme (GDPR "right to be forgotten") haklarına sahipsiniz. Talepleriniz için **kvkk@growthriq.net** adresine yazabilirsiniz.

## 6. İletişim
- **Veri Sorumlusu:** Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti. (GrowthRIQ)
- **E-posta:** **kvkk@growthriq.net**
- **KEP:** datinova@hs09.kep.tr
`,
  },
  terms: {
    title: 'Kullanım Şartları',
    updated: '1 Şubat 2026',
    body: `
## 1. Hizmet Tanımı
GrowthRIQ, e-ticaret ve e-ihracat işletmelerine yönelik AI destekli büyüme istihbaratı sunan bir SaaS platformudur. Asesman, rapor üretimi, mağaza denetimi ve danışmanlık modüllerini kapsar.

## 2. Üyelik
- 18 yaşından büyük olmanız gerekir.
- Doğru ve güncel bilgi sağlamayı kabul edersiniz.
- Hesap güvenliği size aittir; şifrenizi başkasıyla paylaşmayın.

## 3. Ödeme ve Abonelik
- Ücretli paketler tek seferlik veya abonelik bazlı olabilir.
- Ödemeler İyzico / PayTR güvenli ödeme altyapısı üzerinden alınır (Türkiye merkezli).
- İade politikamız: AI ile üretilen raporların indirilmemesi veya tüketilmemesi koşuluyla satın alma tarihinden itibaren 14 gün içinde iade yapılabilir.

## 4. Kabul Edilemez Kullanım
- Platformu suç teşkil eden faaliyetler için kullanamazsınız.
- Tersine mühendislik, scraping veya yetkisiz erişim girişimleri yasaktır.
- AI çıktılarını rakip ürün geliştirmek için kullanamazsınız.

## 5. Fikri Mülkiyet
GrowthRIQ markası, logosu, asesman soruları ve AI rapor şablonları platformun mülkiyetindedir. Üretilen kişisel raporlarınız size aittir; ticari yayında bizi kaynak olarak göstermeniz yeterlidir.

## 6. Sorumluluk Sınırı
AI raporları yorum ve öneriler içerir; finansal kararlarınızın sorumluluğu kullanıcıya aittir. Veri kaybı, dolaylı zararlar konusunda yasaların izin verdiği azami sınıra kadar sorumlu değiliz.

## 7. Fesih
Hizmeti sebep göstermeksizin "hesap silme" düğmesiyle istediğiniz zaman sonlandırabilirsiniz.

## 8. Uyuşmazlıklar
İşbu sözleşme Türkiye Cumhuriyeti yasalarına tabidir. İstanbul mahkemeleri yetkilidir.
`,
  },
  cookies: {
    title: 'Çerez Politikası',
    updated: '1 Şubat 2026',
    body: `
## 1. Çerez Nedir?
Çerez, web sitesini ziyaret ettiğinizde tarayıcınıza yerleştirilen küçük metin dosyalarıdır. Sayfalar arasında tercihlerinizi hatırlamamıza ve trafik analizine yardımcı olur.

## 2. Kullandığımız Çerez Türleri

### Zorunlu çerezler (her zaman aktif)
- **growthriq_token** — kullanıcı oturum kimliği (JWT)
- **growthriq_lang** — dil tercihi (TR/EN)
- **growthriq_cookie_consent** — çerez izninin durumu

Bu çerezler platformun çalışması için zorunludur ve kapatılamaz.

### İsteğe bağlı / Analitik çerezler
Henüz dış analitik servis (Google Analytics, vb.) kullanmıyoruz. Eklediğimizde size onay isteyeceğiz.

### Pazarlama çerezleri
Kullanmıyoruz.

## 3. Çerez Onayı
Sitemize ilk girişinizde gösterilen banner'dan onayınızı verebilirsiniz. Onayınızı her zaman çerezleri silerek geri çekebilirsiniz.

## 4. Tarayıcı Ayarları
Modern tarayıcılarda çerezleri tamamen kapatma seçeneği bulunur. Ancak zorunlu çerezler kapatılırsa platform düzgün çalışmayabilir.

## 5. İletişim
Sorularınız için: **kvkk@growthriq.net**
`,
  },
  'mesafeli-satis': {
    title: 'Mesafeli Satış Sözleşmesi',
    updated: '1 Şubat 2026',
    body: `
## 1. Taraflar

**SATICI:** Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti. ("GrowthRIQ" markası ile, bundan sonra "SATICI" olarak anılacaktır)
- Adres: Rami Yenimahalle, Palabıyık Sk. No: 22/B D: 13, Eyüpsultan / İstanbul
- Vergi Dairesi / No: Bayrampaşa Vergi Dairesi / 2711882508
- MERSIS No: 0271188250800001
- KEP: datinova@hs09.kep.tr
- E-posta: hi@growthriq.net
- Web: https://growthriq.net

**ALICI:** GrowthRIQ üzerinden ücretli paket satın alan kullanıcı (bundan sonra "ALICI" olarak anılacaktır)

## 2. Konu
İşbu sözleşmenin konusu, ALICI'nın SATICI'ya ait https://growthriq.net web sitesinden elektronik ortamda sipariş verdiği aşağıda nitelikleri belirtilen dijital hizmetin satışı ve teslimi ile ilgili 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri gereğince tarafların hak ve yükümlülüklerinin saptanmasıdır.

## 3. Satışa Konu Hizmet
**Hizmet türü:** Dijital içerik (AI destekli e-ticaret büyüme analiz raporu / abonelik tabanlı SaaS hizmeti)
**Vergiler dahil satış fiyatı:** Sepet aşamasında gösterilen tutar (KDV dahil)
**Ödeme şekli:** Online kredi/banka kartı (3D Secure) — İyzico altyapısı

## 4. Genel Hükümler
- ALICI, satın aldığı dijital hizmetin temel özelliklerini, satış fiyatını ve ödeme şekli ile teslimat süresine ilişkin ön bilgileri okuyup bilgi sahibi olduğunu kabul eder.
- ALICI, işbu sözleşmeyi elektronik ortamda onayladığı taktirde mesafeli sözleşmenin kurulmuş olduğunu kabul eder.

## 5. Cayma Hakkı
Mesafeli Sözleşmeler Yönetmeliği m.15/(ğ) gereğince **dijital içerik teslim edilmeden** önce ALICI, **14 gün içinde** herhangi bir gerekçe göstermeksizin cayma hakkını kullanabilir.

⚠️ **ÖNEMLİ:** Dijital içeriğin (rapor) **indirilmesi veya kullanılması** ile cayma hakkı **sona erer**. Bu durumu açıkça kabul edersiniz.

Cayma talebinizi **hi@growthriq.net** adresine yazarak iletebilirsiniz. Onaylanan iade tutarı 14 iş günü içinde ödeme yaptığınız karta iade edilir.

## 6. Teslimat
Hizmet, ödemenin başarıyla tamamlanmasının ardından **anında** ALICI'nın GrowthRIQ hesabına tanımlanır. Asesman tamamlandığında AI raporu 2-5 dakika içinde üretilir.

## 7. Uyuşmazlık Çözümü
İşbu sözleşmeden kaynaklanan uyuşmazlıklarda, ALICI'nın yerleşim yerinin bulunduğu Tüketici Hakem Heyetleri ve Tüketici Mahkemeleri yetkilidir.

## 8. Yürürlük
İşbu sözleşme ALICI tarafından elektronik ortamda onaylandığı anda yürürlüğe girer ve hizmetin tamamen ifa edilmesi ile sona erer.
`,
  },
  'on-bilgilendirme': {
    title: 'Ön Bilgilendirme Formu',
    updated: '1 Şubat 2026',
    body: `
6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği uyarınca, GrowthRIQ üzerinden alacağınız dijital hizmet için aşağıdaki bilgiler sunulmaktadır.

## 1. Satıcı Bilgileri
- **Resmi Unvan:** Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti. ("GrowthRIQ" markası ile faaliyet göstermektedir)
- **Adres:** Rami Yenimahalle, Palabıyık Sk. No: 22/B D: 13, Eyüpsultan / İstanbul
- **Vergi Dairesi / No:** Bayrampaşa Vergi Dairesi / 2711882508
- **MERSIS No:** 0271188250800001
- **KEP:** datinova@hs09.kep.tr
- **E-posta:** hi@growthriq.net
- **Web:** https://growthriq.net

## 2. Hizmetin Temel Nitelikleri
GrowthRIQ; e-ticaret ve e-ihracat işletmelerine yönelik AI destekli büyüme istihbaratı sunan bir SaaS platformudur. Hizmet kapsamı:
- 87 soruluk dijital olgunluk asesmanı
- AI tarafından üretilen detaylı PDF rapor (stratejik danışmanlık seviyesinde)
- 30/60/90 günlük aksiyon planı
- AI destekli danışman sohbeti
- Mağaza denetim modülü (paket seçimine bağlı)

## 3. Satış Fiyatı ve Ödeme
Sepet aşamasında gösterilen tüm fiyatlar **Türk Lirası (TL)** cinsinden ve **KDV dahildir**. Ödeme; kredi/banka kartı ile **İyzico** 3D Secure altyapısı üzerinden alınır.

## 4. Teslimat
Hizmet, ödemenin başarıyla tamamlanmasının ardından **anında** dijital ortamda teslim edilir. Fiziksel kargo söz konusu değildir.

## 5. Cayma Hakkı (m.15/(ğ) İstisnası)
Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesinin (ğ) bendi uyarınca, **elektronik ortamda anında ifa edilen hizmetler** ve **fiziksel olmayan teslim edilen dijital içerikler** açısından cayma hakkı bulunmamaktadır.

⚠️ Buna rağmen GrowthRIQ, müşteri memnuniyeti için **dijital içeriğin indirilmemiş/kullanılmamış olması koşuluyla** satın alma tarihinden itibaren 14 gün içinde **iade hakkı** tanır.

İade talepleri için: **hi@growthriq.net**

## 6. Şikayet ve İtirazlar
Satıcı ile yaşanan ihtilaflarda ALICI, **Tüketici Hakem Heyetleri** ve **Tüketici Mahkemeleri**'ne başvurabilir. (Her yıl Bakanlık tarafından belirlenen parasal sınırlar dahilinde Hakem Heyetleri yetkilidir.)

## 7. Onay
Aşağıdaki kutucuğu işaretleyerek, işbu Ön Bilgilendirme Formu'nu **okuduğunuzu ve elektronik ortamda kabul ettiğinizi** beyan etmiş olursunuz:

☐ Ön Bilgilendirme Formu'nu okudum ve kabul ediyorum.
`,
  },

  hakkimizda: {
    title: 'Hakkımızda',
    updated: '1 Şubat 2026',
    body: `
## GrowthRIQ Kimdir?
GrowthRIQ, e-ticaret ve e-ihracat şirketlerinin **veriyle büyümesine** odaklanan, yapay zekâ destekli bir büyüme istihbaratı platformudur. Türkiye'de geliştirilmiş, yerel pazar dinamiklerini (Trendyol, Hepsiburada, Amazon TR, KOSGEB destekleri, KVKK) doğrudan içeren bir SaaS ürünüdür.

GrowthRIQ markası **Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti.** tarafından işletilmekte ve Türkiye'de tescillidir.

## Misyonumuz
Her ölçekteki e-ticaret işletmesinin, **danışmanlık bütçesi olmadan** McKinsey/BCG kalitesinde stratejik analiz ve aksiyon planına erişmesini sağlamak.

## Vizyonumuz
2027 yılına kadar Türkiye merkezli **10.000+ e-ticaret şirketi** için stratejik karar destek sistemi haline gelmek ve Türkiye'nin e-ihracat dönüşümünde anchor platform olmak.

## Ne Sunuyoruz?
- **55+ soruluk dijital olgunluk asesmanı** — 10 ana kategori, 4 olgunluk seviyesi
- **AI üretimli detaylı stratejik rapor** — SWOT, gap analizi, 30/60/90 yol haritası
- **4 ücretsiz büyüme aracı** — GEO Görünürlük Checkup, Rakip Radarı, Rapor Analitik, Fiyatlama Motoru
- **Mağaza denetim (audit) modülü** — Web sitesi ve marketplace mağazalarınızın teknik/SEO/içerik skoru
- **AI Danışman** — Verinizle konuşan, kişiselleştirilmiş öneriler üreten asistan

## Değerlerimiz
- **Veri öncelikli:** Tahminle değil, ölçümle karar verin
- **Aksiyon odaklı:** "Ne yapmalıyım?" sorusuna net cevap
- **Şeffaflık:** Skorlarınızın metodolojisi ve veri kaynağı açıktır
- **KVKK uyumu:** Tüm verileriniz Türkiye sınırları içindeki sunucularda işlenir

## Ekip
GrowthRIQ; e-ticaret operasyonu, veri bilimi, AI mühendisliği ve dijital pazarlama alanlarında ortalama 10+ yıl deneyimli profesyonellerden oluşan bir ekip tarafından geliştirilmektedir.

## İletişim
Sorularınız, iş birliği teklifleriniz veya basın talepleriniz için: **hi@growthriq.net** adresine yazabilirsiniz. Daha fazla bilgi için **İletişim** sayfamızı ziyaret edebilirsiniz.
`,
  },

  iletisim: {
    title: 'İletişim',
    updated: '1 Şubat 2026',
    body: `
## Bize Ulaşın
Sorularınız, iş birliği teklifleriniz, basın talepleriniz veya KVKK kapsamındaki haklarınızla ilgili her türlü iletişim için aşağıdaki kanallardan bize ulaşabilirsiniz.

## Şirket Bilgileri
- **Resmi Unvan:** Datinova Eğitim Danışmanlık ve Yazılım Ltd. Şti.
- **Marka:** GrowthRIQ
- **Faaliyet Adresi:** Rami Yenimahalle, Palabıyık Sk. No: 22/B D: 13, Eyüpsultan / İstanbul
- **Vergi Dairesi / No:** Bayrampaşa Vergi Dairesi / 2711882508
- **MERSIS No:** 0271188250800001
- **KEP Adresi:** datinova@hs09.kep.tr

## İletişim Kanalları
- **Genel:** hi@growthriq.net
- **Destek / Teknik:** support@growthriq.net
- **KVKK / Veri Sorumlusu:** kvkk@growthriq.net
- **Faturalama / Muhasebe:** billing@growthriq.net
- **Basın / Medya:** press@growthriq.net
- **İş Geliştirme:** partners@growthriq.net

## Çalışma Saatleri
- **Pazartesi - Cuma:** 09:00 - 18:00 (TRT)
- **Hafta Sonu:** Kapalı (acil destek talepleri 24 saat içinde yanıtlanır)

## Müşteri Hizmetleri
Ücretsiz hesap kullanıcılarımıza e-posta üzerinden destek sunmaktayız. Premium ve Enterprise paket sahiplerine **öncelikli destek** (12 saat içinde dönüş) ve **canlı sohbet** kanalı tahsis edilmiştir.

## Şikayet ve Anlaşmazlık Çözümü
Hizmetlerimizle ilgili herhangi bir şikayetiniz varsa, öncelikle **hi@growthriq.net** adresine yazarak çözüm aramamızı bekleyebilirsiniz. Çözülemeyen ihtilaflarda **Tüketici Hakem Heyetleri** ve **Tüketici Mahkemeleri** yetkilidir.

## Hata Bildirimi (Bug Bounty)
Güvenlik açığı veya kritik bir bug fark ederseniz, sorumlu açıklama (responsible disclosure) prensibi ile **security@growthriq.net** adresine yazabilirsiniz. Geri dönüş 48 saat içindedir.
`,
  },

  'iade-iptal': {
    title: 'İade ve İptal Politikası',
    updated: '1 Şubat 2026',
    body: `
## 1. Genel İlke
GrowthRIQ, **dijital içerik** ve **abonelik tabanlı SaaS hizmeti** sunmaktadır. Mesafeli Sözleşmeler Yönetmeliği m.15/(ğ) uyarınca, dijital içerikler için cayma hakkı kanunen mevcut olmamakla birlikte, **müşteri memnuniyeti** kapsamında aşağıdaki esnek iade politikasını uyguluyoruz.

## 2. İade Hakkı Tanınan Durumlar
İade talebiniz aşağıdaki tüm koşullar sağlandığında **onaylanır**:

✅ Satın alma tarihinden itibaren **14 gün içinde** talep edilmesi
✅ AI raporunun **henüz indirilmemiş** veya **görüntülenmemiş** olması
✅ Asesmana **başlanmamış** olması (ya da %25'in altında ilerleme)
✅ Yıllık aboneliklerde aboneliğin **ilk 7 günü** içinde olması
✅ Kötüye kullanım (abuse) tespit edilmemiş olması

## 3. İade Hakkı Tanınmayan Durumlar
Aşağıdaki durumlarda iade **yapılamaz**:

❌ Rapor PDF olarak indirilmiş veya e-posta ile gönderilmişse
❌ Asesman tamamlanmış ve detaylı rapor üretilmişse
❌ AI Danışman özelliği kullanılmışsa (5'ten fazla mesaj)
❌ Aboneliğin 7. gününden sonra
❌ Tek seferlik rapor paketinde rapor üretildikten sonra
❌ Promosyon koduyla %50+ indirim alınan satın almalar (politikalar farklı)

## 4. İade Süreci
1. **hi@growthriq.net** adresine "İade Talebi" konulu bir e-posta gönderin
2. E-postanızda şunlar bulunsun:
   - Sipariş numaranız (e-posta onayında yer alır)
   - İade gerekçeniz (memnuniyetimiz için, **opsiyonel**)
   - Ödeme yaptığınız kartın son 4 hanesi (doğrulama için)
3. Talebiniz **48 saat içinde** incelenir, onay/red bildirilir
4. Onaylanan iadeler, **5-14 iş günü** içinde ödeme aldığımız karta iade edilir (bankanıza bağlı)

## 5. Abonelik İptali
Aylık veya yıllık abonelikler dilediğiniz zaman **dashboard > Plan ayarları** üzerinden iptal edilebilir. İptal sonrası:
- **Mevcut dönem sonuna kadar** hizmet kullanımı devam eder
- Otomatik yenileme yapılmaz
- Hesabınız ve verileriniz korunmaya devam eder (silmek isterseniz "Hesabımı sil" düğmesini kullanın)

## 6. Kısmi İade Politikası
**Yıllık abonelik**te 7. günden sonra iptal halinde:
- Kullanılan ay sayısı × aylık eşdeğer fiyat hesaplanır
- Kalan tutar iade edilir (örnek: yıllık 1.200 ₺ → 4 ay kullanım → 800 ₺ iade)

## 7. Sistem Kaynaklı Sorunlar
GrowthRIQ kaynaklı bir teknik sorun nedeniyle hizmeti alamadıysanız (rapor üretilmedi, API hata verdi vb.), **tam iade** yapılır VEYA hizmet karşılığı yeniden sunulur. Tercih sizindir.

## 8. Yasal Haklarınız
Bu politika, **Tüketici Hakları Kanunu** ve **Mesafeli Sözleşmeler Yönetmeliği** kapsamındaki yasal haklarınızı kısıtlamaz. Anlaşmazlık halinde **Tüketici Hakem Heyetleri** ve **Tüketici Mahkemeleri**'ne başvurabilirsiniz.

## 9. İletişim
Tüm iade ve iptal soruları için: **hi@growthriq.net**
`,
  },

  gizlilik: {
    title: 'Gizlilik Politikası',
    updated: '1 Şubat 2026',
    body: `
GrowthRIQ olarak gizliliğinize değer veriyoruz. Bu Gizlilik Politikası, web sitemizi ve hizmetlerimizi kullandığınızda hangi bilgilerin toplandığını, nasıl kullanıldığını ve nasıl korunduğunu açıklar. Bu politika **Kullanım Şartları**, **KVKK Aydınlatma Metni** ve **Çerez Politikası** ile birlikte değerlendirilmelidir.

## 1. Topladığımız Bilgiler

### 1.1. Sizin Sağladığınız Bilgiler
- Hesap oluşturma sırasında: ad, soyad, e-posta, şirket adı, şifre (hash'lenmiş olarak saklanır)
- Şirket profili: sektör, ciro, çalışan sayısı, web sitesi
- Asesman cevapları: 55+ soruya verdiğiniz yanıtlar
- Ödeme bilgileri: kart bilgileri **bize iletilmez**, doğrudan İyzico altyapısında işlenir

### 1.2. Otomatik Toplanan Bilgiler
- IP adresi ve coğrafi bölge (KVKK uyumlu, anonimleştirilmiş)
- Tarayıcı türü ve versiyonu, cihaz bilgisi
- Sayfa görüntülemeleri, oturum süreleri (saatlik aggregate olarak)
- Çerez verileri (detay için Çerez Politikası)

### 1.3. Üçüncü Taraflardan Alınan Bilgiler
- Şu an aktif değil; eklendiğinde size bilgi verilir (örnek: Google Login gelecekte eklenirse)

## 2. Bilgilerinizi Nasıl Kullanırız?
- ✅ Hizmeti sunmak ve raporu üretmek için (zorunlu)
- ✅ Hesap güvenliği ve dolandırıcılık önleme
- ✅ Fatura kesimi ve ödeme işlemleri
- ✅ Müşteri desteği sağlamak
- ✅ Onayınız varsa, e-posta drip kampanyaları (haftada en fazla 1)
- ❌ **Asla**: verilerinizi 3. taraflara satmak için kullanmayız

## 3. Veri Saklama Süresi
- **Aktif hesaplar:** Hizmet süresince + 1 yıl
- **Pasif hesaplar:** 2 yıl boyunca veri korunur, sonrasında anonim hale getirilir
- **Fatura verileri:** Vergi mevzuatı gereği **10 yıl** boyunca saklanır
- **Pazarlama listeleri:** Onay iptaline kadar; iptal halinde 30 gün içinde silinir

## 4. Veri Güvenliği
- Tüm trafik **TLS 1.3** ile şifrelidir (HTTPS)
- Şifreler **bcrypt** ile hash'lenir, asla düz metin olarak saklanmaz
- Veritabanı **Türkiye merkezli** (veya AB) sunucularda barındırılır
- 2FA opsiyonel olarak desteklenir (yakında)
- Erişim logları 90 gün boyunca tutulur ve düzenli denetlenir

## 5. Üçüncü Taraf Hizmet Sağlayıcılar
Hizmetin sunumu için aşağıdaki kuruluşlarla veri paylaşılır (sadece görev kapsamında):
- **İyzico** — ödeme işlemleri
- **Resend / MailerSend** — transactional e-posta
- **Anthropic / OpenAI / Google** — AI rapor üretimi (anonim asesman verisi gönderilir, kişisel veri içermez)
- **Neon / PostgreSQL** — veritabanı barındırma

## 6. Çocukların Gizliliği
GrowthRIQ, 18 yaş altı kullanıcılara hizmet sunmaz. 18 yaş altı bir kişinin verisi farkında olmadan toplandıysa, **kvkk@growthriq.net** adresine yazıldığında en geç 7 gün içinde silinir.

## 7. Uluslararası Veri Transferi
AI servis sağlayıcıları (Anthropic, OpenAI, Google) merkezleri ABD veya AB'de olabilir. Bu transferler **standart sözleşme maddeleri (SCC)** veya yeterlilik kararı kapsamında yapılır.

## 8. Haklarınız
KVKK m.11 uyarınca:
- 🔍 **Erişim hakkı** — hangi verilerinizin işlendiğini sorabilirsiniz
- ✏️ **Düzeltme hakkı** — yanlış verileri düzelttirebilirsiniz
- 🗑 **Silme hakkı** — "right to be forgotten"
- 🚫 **İtiraz hakkı** — işlemeye itiraz edebilirsiniz
- 📤 **Veri taşınabilirliği** — verilerinizi makine-okur formatta talep edebilirsiniz

Talepleriniz için: **kvkk@growthriq.net** | Yanıt süresi: en fazla 30 gün

## 9. Politikadaki Değişiklikler
Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişikliklerde size e-posta veya panel üzerinden bildirim göndereceğiz. Yukarıdaki "Güncellenme" tarihi en son revizyonu gösterir.

## 10. İletişim
Sorularınız için: **kvkk@growthriq.net** veya **hi@growthriq.net**
`,
  },
};

function md(text) {
  // Minimal markdown -> JSX (h2/h3, bullets, paragraphs, bold)
  const lines = text.trim().split('\n');
  const out = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length) {
      out.push(<ul key={`ul-${key}`} className="list-disc pl-6 space-y-1 text-white/80 mb-4">{listBuf.map((l, i) => <li key={i} dangerouslySetInnerHTML={{ __html: l }} />)}</ul>);
      listBuf = [];
    }
  };
  lines.forEach((line, i) => {
    if (line.startsWith('## ')) { flushList(i); out.push(<h2 key={i} className="text-2xl font-bold mt-8 mb-3">{line.slice(3)}</h2>); }
    else if (line.startsWith('### ')) { flushList(i); out.push(<h3 key={i} className="text-lg font-semibold mt-5 mb-2 text-[#3CD9B8]">{line.slice(4)}</h3>); }
    else if (line.startsWith('- ')) {
      const html = line.slice(2).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      listBuf.push(html);
    } else if (line.trim() === '') { flushList(i); }
    else {
      flushList(i);
      const html = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>');
      out.push(<p key={i} className="text-white/80 leading-relaxed mb-3" dangerouslySetInnerHTML={{ __html: html }} />);
    }
  });
  flushList('end');
  return out;
}

export default function LegalPage({ slug }) {
  const navigate = useNavigate();
  const { language, t, overrides } = useLanguage();
  const content = CONTENT[slug];
  const enBody = EN_BODIES[slug];
  const useEnglish = language === 'en' && enBody;
  const displayTitle = language === 'en' ? (EN_TITLES[slug] || content?.title) : content?.title;
  // Admin CMS override (only for hakkimizda + iletisim where we expose editing)
  const overrideKey = `${slug}.body`;
  const overrideBody = overrides?.[overrideKey];
  const displayBody = overrideBody || (useEnglish ? enBody : content?.body);

  useEffect(() => {
    if (content) document.title = `${displayTitle} — GrowthRIQ`;
  }, [content, displayTitle]);

  if (!content) {
    return <div className="min-h-screen bg-[#0F1B36] text-white flex items-center justify-center">{language === 'en' ? 'Page not found.' : 'Sayfa bulunamadı.'}</div>;
  }

  // KVKK is intentionally Turkish-only (binding under TR law). Show a notice in EN.
  const showKvkkNotice = language === 'en' && slug === 'kvkk';

  return (
    <div className="min-h-screen bg-[#0F1B36] text-white" data-testid={`legal-${slug}`}>
      <header className="border-b border-white/10 bg-[#0F1B36]/80 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Sparkle size={22} weight="fill" className="text-[#3CD9B8]" /><span className="font-bold tracking-tight">GrowthRIQ</span></Link>
          <button onClick={() => navigate(-1)} className="text-sm text-white/70 inline-flex items-center gap-1"><ArrowLeft size={14} /> {language === 'en' ? 'Back' : 'Geri'}</button>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold tracking-tight mb-2">{displayTitle}</h1>
        <p className="text-xs text-white/50 mb-8">{t('legal.updated')} {content.updated}</p>
        {showKvkkNotice && (
          <p className="text-xs text-amber-400/80 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 mb-6">
            Note: This privacy notice is published in Turkish under Turkish law (KVKK / Law No. 6698). The Turkish text below is the binding version. Contact kvkk@growthriq.net for an English summary upon request.
          </p>
        )}
        <div className="prose prose-invert">{md(displayBody)}</div>
        <div className="mt-12 pt-6 border-t border-white/10 text-sm text-white/50 flex flex-wrap gap-x-4 gap-y-2">
          <Link to="/hakkimizda" className="hover:text-[#3CD9B8]">Hakkımızda</Link>
          <Link to="/iletisim" className="hover:text-[#3CD9B8]">İletişim</Link>
          <Link to="/legal/kvkk" className="hover:text-[#3CD9B8]">{t('legal.kvkk')}</Link>
          <Link to="/legal/gizlilik" className="hover:text-[#3CD9B8]">Gizlilik</Link>
          <Link to="/legal/terms" className="hover:text-[#3CD9B8]">{t('legal.terms')}</Link>
          <Link to="/legal/cookies" className="hover:text-[#3CD9B8]">{t('legal.cookies')}</Link>
          <Link to="/legal/mesafeli-satis" className="hover:text-[#3CD9B8]">Mesafeli Satış</Link>
          <Link to="/legal/on-bilgilendirme" className="hover:text-[#3CD9B8]">Ön Bilgilendirme</Link>
          <Link to="/legal/iade-iptal" className="hover:text-[#3CD9B8]">İade & İptal</Link>
        </div>
      </article>
    </div>
  );
}
